import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const MediaJournal = ({ onEntrySubmit }) => {
  const { t } = useTranslation();
  const [entryType, setEntryType] = useState('text');
  const [textContent, setTextContent] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  
  const moods = {
    very_low: { label: 'Very Low', color: 'bg-red-100 text-red-800', emoji: '😞' },
    low: { label: 'Low', color: 'bg-orange-100 text-orange-800', emoji: '😔' },
    neutral: { label: 'Neutral', color: 'bg-yellow-100 text-yellow-800', emoji: '😐' },
    good: { label: 'Good', color: 'bg-green-100 text-green-800', emoji: '😊' },
    excellent: { label: 'Excellent', color: 'bg-blue-100 text-emerald-800', emoji: '😄' }
  };

  const startRecording = async () => {
    try {
      const constraints = entryType === 'video' 
        ? { video: true, audio: true }
        : { audio: true };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (entryType === 'video' && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const mimeType = entryType === 'video' ? 'video/webm' : 'audio/webm';
        const blob = new Blob(chunks, { type: mimeType });
        setMediaBlob(blob);
        setMediaUrl(URL.createObjectURL(blob));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.success(`Started ${entryType} recording`);
    } catch (error) {
      toast.error(`Failed to start ${entryType} recording: ${error.message}`);
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Recording saved');
    }
  };
  
  const clearRecording = () => {
    setMediaBlob(null);
    setMediaUrl(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };
  
  const submitEntry = async () => {
    if (entryType === 'text' && !textContent.trim()) {
      toast.error('Please write something in your journal');
      return;
    }
    
    if ((entryType === 'audio' || entryType === 'video') && !mediaBlob) {
      toast.error(`Please record ${entryType} content`);
      return;
    }
    
    setSubmitting(true);
    
    try {
      let entryData;
      
      if (entryType === 'text') {
        entryData = {
          content: textContent,
          entry_type: 'text',
          mood_level: selectedMood || null
        };
      } else {
        // For audio/video, we'll use a placeholder URL for now
        // In a production app, you'd upload to cloud storage first
        const mediaUrl = `${entryType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.webm`;
        
        entryData = {
          content: `${entryType.charAt(0).toUpperCase() + entryType.slice(1)} journal entry`,
          entry_type: entryType,
          audio_url: entryType === 'audio' ? mediaUrl : null,
          video_url: entryType === 'video' ? mediaUrl : null,
          mood_level: selectedMood || null
        };
      }
      
      await onEntrySubmit(entryData);
      
      // Reset form
      setTextContent('');
      setSelectedMood('');
      clearRecording();
      toast.success('Journal entry saved! AI insights generated.');
      
    } catch (error) {
      toast.error('Failed to save journal entry');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Card className="glass-card p-6" data-testid="media-journal-form">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        📝 {t('media.newEntry')}
      </h2>
      
      <Tabs value={entryType} onValueChange={setEntryType} className="mb-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="text" data-testid="text-journal-tab">
            📝 {t('media.text')}
          </TabsTrigger>
          <TabsTrigger value="audio" data-testid="audio-journal-tab">
            🎤 {t('media.audio')}
          </TabsTrigger>
          <TabsTrigger value="video" data-testid="video-journal-tab">
            🎥 {t('media.video')}
          </TabsTrigger>
        </TabsList>
        
        {/* Mood Selection */}
        <div className="mt-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('media.selectMood')}
          </label>
          <Select value={selectedMood} onValueChange={setSelectedMood}>
            <SelectTrigger className="w-full" data-testid="mood-select">
              <SelectValue placeholder={t('media.selectMood')} />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(moods).map(([key, mood]) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center">
                    <span className="mr-2">{mood.emoji}</span>
                    {mood.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <TabsContent value="text">
          <div className="space-y-4">
            <Textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder={t('media.typeHere')}
              className="min-h-[120px] custom-input resize-none"
              data-testid="journal-textarea"
            />
          </div>
        </TabsContent>
        
        <TabsContent value="audio">
          <div className="space-y-4">
            <div className="text-center">
              {!isRecording && !mediaBlob && (
                <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="text-4xl mb-4">🎤</div>
                  <p className="text-gray-600 mb-4">
                    Record a voice message to capture your thoughts and feelings
                  </p>
                  <Button 
                    onClick={startRecording}
                    className="btn-primary"
                    data-testid="start-audio-recording-btn"
                  >
                    Start Recording
                  </Button>
                </div>
              )}
              
              {isRecording && (
                <div className="p-8 bg-red-50 rounded-lg">
                  <div className="text-4xl mb-4 animate-pulse">🔴</div>
                  <p className="text-red-600 font-medium mb-4">Recording in progress...</p>
                  <Button 
                    onClick={stopRecording}
                    variant="outline"
                    className="btn-secondary"
                    data-testid="stop-recording-btn"
                  >
                    Stop Recording
                  </Button>
                </div>
              )}
              
              {mediaBlob && mediaUrl && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-4xl mb-4">✅</div>
                  <p className="text-green-600 font-medium mb-4">Audio recorded successfully!</p>
                  <audio ref={audioRef} controls src={mediaUrl} className="mb-4" />
                  <div className="flex space-x-2">
                    <Button 
                      onClick={clearRecording}
                      variant="outline"
                      className="btn-secondary"
                      data-testid="clear-audio-btn"
                    >
                      Record Again
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="video">
          <div className="space-y-4">
            <div className="text-center">
              {!isRecording && !mediaBlob && (
                <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="text-4xl mb-4">🎥</div>
                  <p className="text-gray-600 mb-4">
                    Record a video message to share your thoughts and emotions
                  </p>
                  <Button 
                    onClick={startRecording}
                    className="btn-primary"
                    data-testid="start-video-recording-btn"
                  >
                    Start Video Recording
                  </Button>
                </div>
              )}
              
              {isRecording && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="text-4xl mb-4 animate-pulse">🔴</div>
                  <p className="text-red-600 font-medium mb-4">Recording video...</p>
                  <video 
                    ref={videoRef} 
                    className="w-full max-w-md mx-auto rounded-lg mb-4" 
                    muted
                  />
                  <Button 
                    onClick={stopRecording}
                    variant="outline"
                    className="btn-secondary"
                    data-testid="stop-video-recording-btn"
                  >
                    Stop Recording
                  </Button>
                </div>
              )}
              
              {mediaBlob && mediaUrl && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-4xl mb-4">✅</div>
                  <p className="text-green-600 font-medium mb-4">Video recorded successfully!</p>
                  <video controls src={mediaUrl} className="w-full max-w-md mx-auto rounded-lg mb-4" />
                  <div className="flex space-x-2">
                    <Button 
                      onClick={clearRecording}
                      variant="outline"
                      className="btn-secondary"
                      data-testid="clear-video-btn"
                    >
                      Record Again
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      <Button 
        onClick={submitEntry}
        disabled={submitting || (
          entryType === 'text' ? !textContent.trim() : !mediaBlob
        )}
        className="btn-primary w-full"
        data-testid="save-entry-btn"
      >
        {submitting ? t('media.saving') : t('media.save')}
      </Button>
      
      {entryType !== 'text' && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            💡 <strong>Tip:</strong> {entryType === 'audio' 
              ? 'Speak naturally about your thoughts, feelings, or experiences. The AI will analyze the content for insights.' 
              : 'Share your emotions visually and verbally. This creates a richer record of your wellness journey.'}
          </p>
        </div>
      )}
    </Card>
  );
};

export default MediaJournal;
